import { InputAdornment, TextField } from '@mui/material';
import _debounce from 'lodash/debounce';
import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { Company } from '../../../model/user/Company';
import { Pagination } from '../../../shared/components/Pagination';
import { setSelectedSupplier, deselectSupplier } from '../../../store/brand/clientSlice';
import { IAllStates } from '../../../store/rootReducer';
import EmptyState from '../../../shared/components/placeholder/EmptyState';
import { useTranslation } from 'react-i18next';
import { truncateString } from '../../../shared/functions/Functions';
import TooltipOverlay from '../../../shared/components/tooltip/Tooltip';
import { CustomImageHandler } from '../../../shared/components/CustomImageHandler';
import { useClientSuppliers } from '../../hooks/useClientSuppliers';

interface IFindSuppliersProps {
    closeModal?: () => void;
    renderPagination?: boolean;
    idScroll?: string;
}

export function FindSuppliers({ closeModal, renderPagination = false, idScroll }: IFindSuppliersProps) {
    const dispatch = useDispatch();
    const isMobile = window.innerWidth <= 700;
    const { selectedSupplier } = useSelector((state: IAllStates) => state.client);
    const { suppliers, totalPages, filterParams, count, setFilterParams } = useClientSuppliers();
    const { t } = useTranslation();
    const [supplierList, setSupplierList] = useState<Company[]>([]);

    const handleDeselectSupplier = () => {
        dispatch(deselectSupplier());
        if (closeModal) {
            closeModal();
        }
    };
    const selectSupplier = (supplier: Company) => {
        if (selectedSupplier?.id !== supplier.id) {
            dispatch(setSelectedSupplier(supplier));
            if (closeModal) {
                closeModal();
            }
        }
    };

    useEffect(() => {
        setSupplierList(prev => (filterParams.page === 1 ? suppliers : [...prev, ...suppliers]));
    }, [suppliers]);

    const handleChange = _debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = event.target;
        setFilterParams(prevState => ({ ...prevState, [name]: value, page: 1 }));
    }, 300);

    return (
        <>
            <Row className="justify-content-center">
                {!isMobile && (
                    <Col onClick={handleDeselectSupplier} className="mb-3 cursor-pointer d-md-none text-center">
                        <Button className="btn-outline-primary w-sm-50 w-100">
                            <span>Select all suppliers</span>
                            <img
                                src={'/icons/img-all-users.png'}
                                className="ml-2"
                                width="20"
                                height="20"
                                alt="User photo"
                                onError={(e: any) => (
                                    (e.target.onerror = null), (e.target.src = '/icons/UserPlaceholder.svg')
                                )}
                            />
                        </Button>
                    </Col>
                )}

                <Col md={6} className="mb-3">
                    <TextField
                        name="displayName"
                        label="Supplier search"
                        variant="outlined"
                        size="small"
                        onChange={handleChange}
                        className="w-sm-50 w-100"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" className="pr-2">
                                    <FaSearch className="text-muted cursor-pointer" size={16} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Col>
            </Row>
            {supplierList?.length ? (
                <div id={idScroll ?? 'scrollableClientSuppliers'} className="w-100 overflow-auto holder-row px-3">
                    <InfiniteScroll
                        scrollableTarget={idScroll ?? 'scrollableClientSuppliers'}
                        dataLength={supplierList?.length || 0}
                        className="row agency-creator-row"
                        next={() => {
                            setFilterParams((prev: any) => ({ ...prev, page: prev.page + 1 }));
                        }}
                        endMessage={
                            !renderPagination && (
                                <div className="col-12">
                                    <p className="text-center">
                                        <b>{t('agency.seen_all_clients')}</b>
                                    </p>
                                </div>
                            )
                        }
                        hasMore={renderPagination ? false : (supplierList?.length || 0) < Number(count)}
                        loader={
                            !renderPagination && (
                                <div className="col-12">
                                    <h4>Loading...</h4>
                                </div>
                            )
                        }
                    >
                        {supplierList?.map((item, i: number) => {
                            const isSelected = item.id === selectedSupplier?.id;
                            const name = item.displayName ?? '';
                            const truncatedName = truncateString(name, 20);
                            const isTruncated = name.length > 20;

                            return (
                                <Col
                                    onClick={() => selectSupplier(item)}
                                    xs={4}
                                    lg={3}
                                    className="mb-3 cursor-pointer agency-creator-item"
                                    key={`${item.displayName}-${i}`}
                                >
                                    <div
                                        className={`d-flex flex-column align-items-center ${
                                            isSelected ? 'selected-user' : ''
                                        }`}
                                    >
                                        <div className="position-relative">
                                            <CustomImageHandler
                                                thumbnailPath={item?.profilePhoto?.thumbnail}
                                                photoPath={item?.profilePhoto?.path}
                                                altTag="Supplier"
                                                classes={`agency-creator-avatar ${
                                                    isSelected ? 'selected-agency-photo' : ''
                                                } rounded-circle img-cover shadow user-img-rounded`}
                                                name={item?.displayName}
                                            />
                                            {isSelected && (
                                                <p
                                                    className="mb-0 active-badge"
                                                    style={{ bottom: -8, left: 'calc(50% - 27.5px)' }}
                                                >
                                                    Active
                                                </p>
                                            )}
                                        </div>
                                        <TooltipOverlay content={isTruncated ? name : ''}>
                                            <h6 className="mb-0 mt-3">{truncatedName}</h6>
                                        </TooltipOverlay>
                                    </div>
                                </Col>
                            );
                        })}
                    </InfiniteScroll>
                    {renderPagination && totalPages > 1 && (
                        <div className="row mt-3">
                            <div className="col-12 d-flex justify-content-end">
                                <Pagination
                                    page={filterParams?.page}
                                    totalPages={totalPages}
                                    handlePagination={newPage => {
                                        setFilterParams(prev => ({ ...prev, page: newPage }));
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <EmptyState desc={t('agency.user_empty')} btnText={t('agency.add_more_creators')} />
            )}
        </>
    );
}
